import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { getImageFile } from 'utils/stringUtils';
import Header from 'containers/AssessmentResult/components/Header';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import { Typography, makeStyles, Box } from '@material-ui/core';
import Summary from 'components/SurveyResult/components/Summary';
import OverallComponent from 'components/SurveyResult/components/OverallComponent';
import useSiteCopySelector from 'components/useSiteCopySelector';
import RegularAccordionComponent from 'components/SurveyResult/components/RegularAccordionComponent';

const useStyles = makeStyles(theme => ({
  header: {
    padding: props => (props.small ? 0 : '25px 0px'),
    marginBottom: theme.spacing(3),

    '& .MuiButton-root': {
      alignSelf: 'flex-start',
    },

    '& h6': {
      fontSize: props => props.small && '0.75rem',
    },

    '& h4': {
      fontSize: props => props.small && '1rem',
    },
  },
  section: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    padding: props => (props.small ? theme.spacing(2) : 40),
  },
  resultsContainer: {
    display: 'flex',
    flexDirection: props => props.small && 'column-reverse',
    marginTop: theme.spacing(2),
  },
  resultsImageContainer: {
    display: 'flex',
    flexDirection: props => !props.small && 'column',
    alignItems: 'center',
    padding: 10,
    borderRadius: 20,
    minWidth: 230,
    width: props => (props.small ? '100%' : '30%'),
    alignSelf: 'flex-start',
    marginLeft: props => !props.small && theme.spacing(3),
    marginBottom: props => props.small && theme.spacing(2),
    backgroundColor: props => props.backgroundColor,

    '& img': {
      height: props => (props.small ? 50 : 150),
      marginBottom: props => !props.small && 20,
      marginRight: props => props.small && theme.spacing(3),
    },

    '& p': {
      ...theme.typography.subtitle2,
      fontSize: props => (props.small ? '0.875rem' : '1.125rem'),
    },
  },
}));

export const SeriesAssessmentResult = ({
  assessment,
  answers,
  assessmentResult,
  actionsNode,
  showShareButton,
  linkAsPlainText,
  small,
}) => {
  const { scoringsObject } = assessmentResult;
  const {
    overallComponent,
    overallLabel,
    overallScoring,
    regularComponents,
    trend,
    scoring,
  } = scoringsObject;
  const bgColorMapping = {
    better: '#E4FFF3',
    worse: '#FFF8F3',
  };
  const classes = useStyles({ backgroundColor: bgColorMapping[trend], small });
  const [courseSiteCopy] = useSiteCopySelector(['courses']);

  if (overallComponent) {
    overallComponent.overallScoring = overallScoring;
    overallComponent.result = null;
  }
  let generalScoreComponents = [];
  if (_get(assessmentResult.results, 'items') && !_isEmpty(scoring)) {
    generalScoreComponents = regularComponents.map((el, index) => {
      let finalIndex = index;
      if (overallComponent && overallComponent.overallScoreResultLinks)
        finalIndex = index + 1;
      return {
        ...el,
        result: assessmentResult.results.items[finalIndex],
        scoring: scoring[index],
        index,
      };
    });
  }

  const {
    fields: { name, globalOptions, resultsIntro },
    sys: { id },
  } = assessment;

  const isFullWidth = _get(globalOptions, 'fullWidth', false);
  const showSummary = _get(globalOptions, 'showSummary', false);

  const userData = {
    gender: _get(answers, 'gender'),
    age: _get(answers, 'dob'),
  };

  const imageUrl = getImageFile(assessment.fields);
  const courseImageUrl =
    trend && _get(assessment.fields, ['context', 'courseImage', trend]);
  const overallResult =
    _get(overallComponent, 'overallScoreResultLinks') &&
    assessmentResult.results.items[0];
  const filteredComponents = generalScoreComponents.filter(
    component =>
      component != null &&
      !_isEmpty(component.result) &&
      component.scoring !== -1,
  );

  return (
    <>
      <Header
        className={classes.header}
        image={imageUrl}
        title={name}
        isFullWidth={isFullWidth}
        assessmentId={id}
        invisiblePdfContent={false}
        answers={answers}
        actionsNode={<Box mt={1}>{actionsNode}</Box>}
        showShareButton={showShareButton}
        showDivider={false}
      />
      {!overallComponent && !_isEmpty(resultsIntro) && (
        <RichTextReactRenderer
          data={resultsIntro}
          paragraphVariant="body2"
          linkAsPlainText={linkAsPlainText}
        />
      )}
      <section className={classes.section}>
        {overallComponent && (
          <OverallComponent
            overallComponent={overallComponent}
            inCrisisComponent={null}
            intro={resultsIntro}
            userData={userData}
            isFullWidth={isFullWidth}
            assessment={assessment}
            hideResources
            invisiblePdfContent={false}
            hideSignUpButton
          />
        )}
        <div className={classes.resultsContainer}>
          <div>
            {overallResult && (
              <RichTextReactRenderer
                className={classes.descriptionWrapper}
                data={_get(overallResult, 'fields.description')}
                paragraphVariant="body2"
                linkAsPlainText={linkAsPlainText}
              />
            )}
            {showSummary && (
              <Summary
                overall={overallComponent}
                components={[]}
                options={globalOptions}
                id=""
              />
            )}
            {filteredComponents.map(component => (
              <RegularAccordionComponent
                key={component.labelValue}
                isFullWidth={isFullWidth}
                withAccordion={false}
                assessmentId={id}
                assessment={assessment}
                component={component}
                userData={userData}
                options={globalOptions}
                invisiblePdfContent
                linkAsPlainText
              />
            ))}
          </div>
          {courseImageUrl && (
            <div className={classes.resultsImageContainer}>
              <img src={courseImageUrl} alt="Results" />
              <Typography color="textPrimary" align="center">
                {overallLabel} / {_get(courseSiteCopy, ['pageCopy', trend])}
              </Typography>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
